import React, { useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import FormatCurrency from 'components/Common/FormatCurrency'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import OfferBookButton from 'components/OfferPage/OfferBookButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import Caption from 'components/Luxkit/Typography/Caption'

interface Props {
  packageView: OfferPackageOptionView;
  rooms: Array<App.Occupants>;
  offer: App.Offer;
  checkIn?: string;
  isFlightPrice?: boolean;
  canBuyNow?: boolean;
  valuedUpTo?: {
    value: number;
    percent: number;
  };
}

function AlwaysOnPriceDetails(props: Props) {
  const {
    rooms,
    offer,
    packageView,
    checkIn,
    isFlightPrice,
    canBuyNow,
    valuedUpTo,
  } = props

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const pkg = packageView.package

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const hotelPrice = packageView.hotelPrice
  const exclusivePriceColor = useHotelPriceColour(packageView.taxesAndFees)

  let totalPrice = (isFlightPrice ? hotelPrice + packageView.flightPrice : hotelPrice)
  totalPrice = showTaxExclusivePrices ? totalPrice : totalPrice + packageView.propertyFees
  let totalValuedUp = (valuedUpTo ? valuedUpTo.value : packageView.hotelValue) + (isFlightPrice ? packageView.flightPrice : 0)
  totalValuedUp = showTaxExclusivePrices ? totalValuedUp : totalValuedUp + packageView.propertyFees
  const showPriceDetails = packageView.taxesAndFees > 0 || packageView.propertyFees > 0
  const shouldShowValue = packageView.hotelValue > packageView.hotelPrice && !pkg.roomRate?.inclusionsHideValue
  const businessCreditPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + packageView.propertyFees

  return <Group direction="vertical" gap={4}>
    <div>
      <Caption variant="large">
        Total for {pkg.durationLabel.toLowerCase()} {!checkIn && 'from'}
      </Caption>
      <BodyText variant="large" weight="bold" colour={exclusivePriceColor}>
        <FormatCurrency value={totalPrice} format="roundedDollar" />
      </BodyText>
      <TaxesAndFees
        taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
        taxAmount={packageView.taxesAndFees}
      />
      {shouldShowValue && <Caption variant="medium" weight="bold">
        Valued up to <FormatCurrency value={totalValuedUp} />
      </Caption>}
    </div>
    {showIncludedGuestsExceeded && <AdditionalGuestsPopup
      complex={false}
      description="+ Extra guest surcharge"
      modalContent={pkg.roomPolicyDescription}
    />}
    {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
      type="exact"
      offer={offer}
      totalCost={businessCreditPrice}
      numberOfNights={pkg.duration}
    />}
    {showPriceDetails && <PriceDetailsModal
      offerPage
      offer={offer}
      duration={pkg.duration}
      taxesAndFees={packageView.taxesAndFees}
      propertyFees={packageView.propertyFees}
      hotelPrice={hotelPrice}
      flightPrice={isFlightPrice ? packageView.flightPrice : undefined}
      rooms={rooms}
      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
      dueAtPropertyMessage={offer.property.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
      cta={canBuyNow &&
        <OfferBookButton
          type="submit"
          pkg={packageView.package}
        >
          Book now
        </OfferBookButton>
      }
    />}
  </Group>
}

export default AlwaysOnPriceDetails
